import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flame, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ct = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', weekday: 'short', hour: 'numeric', minute: '2-digit' });
const ago = (iso) => { const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)}h ${m % 60}m ago`; };

const StartForm = ({ onDone }) => {
    const [seed, setSeed] = useState('500');
    const [pct, setPct] = useState('20');
    const start = async (when) => {
        try {
            await axios.post(`${API}/admin/daily-super/start`, { seed: Number(seed), bet_pct: Number(pct), when }, { withCredentials: true });
            toast.success(when === '7am' ? 'Daily Super set to start at 7:00 AM Central' : 'Daily Super is live');
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not start'); }
    };
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 max-w-md">
                <label className="text-xs text-white/60">Start amount ($)
                    <Input type="number" value={seed} onChange={(e) => setSeed(e.target.value)} className="mt-1 h-9" data-testid="daily-super-seed-input" />
                </label>
                <label className="text-xs text-white/60">% of each bet added
                    <Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1 h-9" data-testid="daily-super-pct-input" />
                </label>
            </div>
            <div className="flex flex-wrap gap-2">
                <Button onClick={() => start('7am')} className="bg-[#FF3B30] hover:bg-[#ff5a50]" data-testid="daily-super-start-7am-btn">Start at 7:00 AM</Button>
                <Button onClick={() => start('now')} variant="outline" data-testid="daily-super-start-now-btn">Start now</Button>
            </div>
        </div>
    );
};

const PlayerRow = ({ p, onPick }) => (
    <div className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 text-xs text-white/80" data-testid={`daily-super-player-${p.user_id}`}>
        <div className="min-w-0">
            <div className="font-bold text-white truncate">{p.name} {p.active_now && <span className="ml-1 rounded-full bg-[#39FF14]/15 px-1.5 text-[10px] text-[#39FF14]">playing now</span>}</div>
            <div className="text-white/50 truncate">{p.email}{p.last_spin ? ` · bet ${money(p.wagered)} · last spin ${ago(p.last_spin)}` : ''}</div>
        </div>
        <Button size="sm" onClick={() => onPick(p)} className="bg-[#FFD700] text-black hover:bg-[#ffe34d] shrink-0" data-testid={`daily-super-award-${p.user_id}`}>Award</Button>
    </div>
);

const SpeedBox = ({ cur, onDone }) => {
    const [pct, setPct] = useState(String(cur.bet_pct));
    const save = async () => {
        try {
            await axios.post(`${API}/admin/daily-super/rate`, { bet_pct: Number(pct) }, { withCredentials: true });
            toast.success(`Growth set to ${pct}% — the amount players see did not change`);
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not change speed'); }
    };
    return (
        <div className="flex items-end gap-2">
            <label className="text-xs text-white/60">Growth % of each bet
                <Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1 h-9 w-24" data-testid="daily-super-rate-input" />
            </label>
            <Button size="sm" variant="outline" onClick={save} data-testid="daily-super-rate-save">Change speed</Button>
        </div>
    );
};

const WinnerPicker = ({ players, onPick }) => {
    const [q, setQ] = useState('');
    const [others, setOthers] = useState([]);
    const term = q.trim().toLowerCase();
    useEffect(() => {
        if (term.length < 2) { setOthers([]); return undefined; }
        const t = setTimeout(() => axios.get(`${API}/admin/daily-super/search`, { params: { q: term }, withCredentials: true })
            .then(({ data }) => setOthers(data.players)).catch(() => setOthers([])), 300);
        return () => clearTimeout(t);
    }, [term]);
    const match = (p) => `${p.name} ${p.email || ''}`.toLowerCase().includes(term);
    const shown = term ? players.filter(match) : players;
    const ids = new Set(players.map((p) => p.user_id));
    const extra = others.filter((p) => !ids.has(p.user_id));
    return (
        <div data-testid="daily-super-players">
            <div className="mb-1 flex items-center gap-1 text-xs font-bold text-white/70"><Users className="w-3 h-3" /> Pick the winner — search any player by name or email</div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player name or email…" className="mb-2 h-9" data-testid="daily-super-search-input" />
            {shown.length === 0 && extra.length === 0 && <div className="text-xs text-white/40" data-testid="daily-super-no-results">{term ? 'No players match that search.' : 'Nobody has played yet — search a name above.'}</div>}
            <div className="max-h-72 overflow-y-auto pr-1">
                {shown.map((p) => <PlayerRow key={p.user_id} p={p} onPick={onPick} />)}
                {extra.length > 0 && <div className="mt-2 text-[10px] uppercase tracking-widest text-white/40">Other players (haven't played since it started)</div>}
                {extra.map((p) => <PlayerRow key={p.user_id} p={p} onPick={onPick} />)}
            </div>
        </div>
    );
};

const Current = ({ cur, onDone }) => {
    const [pick, setPick] = useState(null);
    const [busy, setBusy] = useState(false);
    const cancel = async () => {
        if (!window.confirm('Cancel this Daily Super? It disappears from the games and nobody is paid.')) return;
        try { await axios.post(`${API}/admin/daily-super/cancel`, {}, { withCredentials: true }); toast.success('Daily Super cancelled'); onDone(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
    };
    const award = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/daily-super/trigger`, { user_id: pick.user_id }, { withCredentials: true });
            toast.success(`${data.winner} won the Daily Super — ${money(data.amount)}`);
            setPick(null); onDone();
        } catch (e) { toast.error(e.response?.data?.detail || 'Trigger failed'); onDone(); }
        setBusy(false);
    };
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest text-white/50">{cur.live ? 'Live on every slot game' : `Starts ${ct(cur.starts_at)} Central`}</div>
                    <div className="text-3xl font-black text-[#FFD700] tabular-nums" data-testid="daily-super-current-amount">{money(cur.amount)}</div>
                    <div className="text-xs text-white/50">{cur.live ? 'Started' : 'Starts'} at {money(cur.seed)} · grows {cur.bet_pct}% of every slot, keno and table bet</div>
                </div>
                <Button variant="outline" onClick={cancel} data-testid="daily-super-cancel-btn">Cancel</Button>
            </div>
            <SpeedBox key={cur.bet_pct} cur={cur} onDone={onDone} />
            {cur.live && <WinnerPicker players={cur.players} onPick={setPick} />}
            <AlertDialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
                <AlertDialogContent data-testid="daily-super-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Pay the Daily Super to {pick?.name}?</AlertDialogTitle>
                        <AlertDialogDescription>About {money(cur.amount)} goes straight into their cash balance right now. This can't be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="daily-super-confirm-cancel">Back</AlertDialogCancel>
                        <AlertDialogAction className="bg-[#FFD700] text-black hover:bg-[#ffe34d] font-bold" disabled={busy} onClick={(e) => { e.preventDefault(); award(); }} data-testid="daily-super-confirm-award">Pay it</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

// Admin: owner-run Daily Super Jackpot (start, watch it grow, award to a chosen slot player).
export const DailySuperAdminPanel = () => {
    const [data, setData] = useState(null);
    const load = useCallback(() => axios.get(`${API}/admin/daily-super`, { withCredentials: true })
        .then(({ data }) => setData(data)).catch(() => toast.error('Could not load Daily Super')), []);
    useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);
    if (!data) return null;
    return (
        <div className="card-dark p-5 space-y-4" style={{ borderColor: '#FF3B3060' }} data-testid="daily-super-admin">
            <div className="flex items-center gap-2"><Flame className="w-5 h-5 text-[#FF3B30]" /><span className="font-bold text-white">Daily Super Jackpot</span></div>
            <p className="text-xs text-white/50">Shows on every slot, keno and table game until you award it. Grows from those bets only. It never drops on its own — you pick the winner.</p>
            {data.current ? <Current cur={data.current} onDone={load} /> : <StartForm onDone={load} />}
            <div className="grid md:grid-cols-2 gap-4 text-xs">
                <div data-testid="daily-super-history">
                    <div className="mb-1 font-bold text-white/70">Past Daily Supers</div>
                    {data.history.length === 0 && <div className="text-white/40">None yet</div>}
                    {data.history.map((h) => <div key={h.id} className="flex justify-between border-b border-white/5 py-1 text-white/70"><span>{(h.fired_at || h.created_at || '').slice(0, 10)} · {h.status}</span><span>{h.winner_name ? `${h.winner_name} ${money(h.amount)}` : ''}</span></div>)}
                </div>
                <div data-testid="mega-community-history">
                    <div className="mb-1 flex items-center gap-1 font-bold text-white/70"><Users className="w-3 h-3" /> MEGA community shares (hitter gets half)</div>
                    {data.mega_payouts.length === 0 && <div className="text-white/40">No MEGA hits yet</div>}
                    {data.mega_payouts.map((m) => <div key={m.id} className="flex justify-between border-b border-white/5 py-1 text-white/70"><span>{m.game_name} · {money(m.pool_amount)}</span><span>{money(m.community_amount)} shared · {m.status}</span></div>)}
                </div>
            </div>
        </div>
    );
};

export default DailySuperAdminPanel;
